import { Dropdown } from '@nextui-org/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import KebabMenuIcon from '../../assets/KebabMenuIcon';
import defaultPicture from '../../assets/pngs/vehicle_default.png';
import IconButton from '../Button/IconButton';
import SimpleSelectDropdown from '../Dropdown/SimpleSelectDropdown';
import Text from '../Text';
import {
  ChipBox,
  CompanyBox,
  DateBox,
  MoneyBox,
  PeopleBox,
  PillBox,
  TextBox,
  TextWithActionsBox,
  VehicleChipBox,
  VehicleCompanyBox,
  VehicleDateBox,
  VehicleMoneyBox,
  VehiclePeopleBox,
  VehiclePillBox,
  VehicleTextBox,
  VehicleTextWithActionsBox,
} from '.';
import { VehicleCardInformation } from './shared/types';
import {
  ButtonsContainer,
  ButtonsMobileContainer,
  Container,
  StyledButton,
  StyledSecondaryButton,
  VariantFourStatusPill,
  VariantOneStatusPill,
  VariantThreeStatusPill,
  VariantTwoStatusPill,
  VehicleBodyContainer,
  VehicleHeaderActionsContainer,
  VehicleHeaderContainer,
  VehicleHeaderInformationContainer,
  VehicleImage,
  VehicleImageContainer,
  VehicleInfoContainer,
  VehicleSmallImageContainer,
  VehicleSubTitle,
  VehicleTitle,
} from './VehicleCard.styles';
import VehicleShimmerBox, { ShimmerBox } from './VehicleShimmerCard';
import VehicleTagsBox, { TagsBox } from './VehicleTagsBox/VehicleTagsBox';

export interface VehicleCardInformationStatus {
  key: string;
  name: string;
}

export interface VehicleCardPill {
  variant: 'variant1' | 'variant2' | 'variant3' | 'variant4';
  text: string;
}

export interface BasicInfoBox {
  title?: string;
  type: string;
  /**
   * Represented in rem.
   * @example if we want 15rem, minWidth=15
   */
  minWidth?: number;
  /**
   * Represented in rem.
   * @example if we want 15rem, maxWidth=15
   */
  maxWidth?: number;
}

export type InfoBox = TextBox | CompanyBox | PeopleBox | MoneyBox | DateBox | ChipBox | TextWithActionsBox;

export interface VehicleCardPopoverItem {
  icon?: React.ReactNode;
  text: string;
  value: string;
  onClick: any;
}

/* eslint-disable @typescript-eslint/indent */
export type VehicleCardAction =
  | {
      variant: 'primary' | 'secondary';
      onClick: any;
      disabled: boolean;
      label: string;
      icon?: React.ReactNode;
      popoverItems?: never;
    }
  | {
      variant: 'primary-popover';
      onClick?: any;
      disabled: boolean;
      label: string;
      icon?: React.ReactNode;
      popoverItems: Array<VehicleCardPopoverItem>;
    };
/* eslint-enable @typescript-eslint/indent */

export type VehicleModalAction = {
  title: string;
  items: Array<VehicleCardPopoverItem>;
};

export interface VehicleCardIconAction {
  isPressed: boolean; // If true, then the activeIcon is displayed.
  defaultIcon: JSX.Element;
  activeIcon?: JSX.Element;
  onClick: any;
}

export interface VehicleAdditionalAction {
  label: string;
  labelColor: string;
  onClick: any;
}

const renderVehiclePill = (pill: VehicleCardPill) => {
  if (pill) {
    switch (pill.variant) {
      case 'variant1':
        return <VariantOneStatusPill>{pill.text}</VariantOneStatusPill>;
      case 'variant2':
        return <VariantTwoStatusPill>{pill.text}</VariantTwoStatusPill>;
      case 'variant3':
        return <VariantThreeStatusPill>{pill.text}</VariantThreeStatusPill>;
      case 'variant4':
        return <VariantFourStatusPill>{pill.text}</VariantFourStatusPill>;
      default:
        break;
    }
  }
  return null;
};

export const renderInfoBoxComponent = (box: InfoBox, key: number) => {
  switch (box?.type) {
    case 'TextBox': {
      const typedBox = box as TextBox;
      return <VehicleTextBox key={key} data={typedBox} />;
    }
    case 'CompanyBox': {
      const typedBox = box as CompanyBox;
      return <VehicleCompanyBox key={key} data={typedBox} />;
    }
    case 'PeopleBox': {
      const typedBox = box as PeopleBox;
      return <VehiclePeopleBox key={key} data={typedBox} />;
    }
    case 'MoneyBox': {
      const typedBox = box as MoneyBox;
      return <VehicleMoneyBox key={key} data={typedBox} />;
    }
    case 'DateBox': {
      const typedBox = box as DateBox;
      return <VehicleDateBox key={key} data={typedBox} />;
    }
    case 'TagsBox': {
      const typedBox = box as TagsBox;
      return <VehicleTagsBox key={key} data={typedBox} />;
    }
    case 'ChipBox': {
      const typedBox = box as ChipBox;
      return <VehicleChipBox key={key} data={typedBox} />;
    }
    case 'TextWithActionsBox': {
      const typedBox = box as TextWithActionsBox;
      return <VehicleTextWithActionsBox key={key} data={typedBox} />;
    }
    case 'PillBox': {
      const typeBox = box as PillBox;
      return <VehiclePillBox key={key} data={typeBox} />;
    }
    case 'ShimmerBox': {
      const typedBox = box as ShimmerBox;
      return <VehicleShimmerBox key={key} data={typedBox} />;
    }
    default:
      return <></>;
  }
};

const renderActionButton = (action: VehicleCardAction) => {
  if (!action) return <></>;
  return action.variant === 'primary' ? (
    <StyledButton $disableMinWidth auto onClick={action.onClick} disabled={action.disabled} icon={action?.icon}>
      {action.label}
    </StyledButton>
  ) : (
    <StyledSecondaryButton
      $disableMinWidth
      auto
      onClick={action.onClick}
      disabled={action.disabled}
      icon={action?.icon}
    >
      {action.label}
    </StyledSecondaryButton>
  );
};

/*
// Uncomment renderIconActionButton after implementing Add to favorite feature
const renderIconActionButton = (iconAction: VehicleCardIconAction) => {
  if (!iconAction) return <></>;
  return (
    <IconButton
      icon={iconAction.isPressed && iconAction.activeIcon ? iconAction.activeIcon : iconAction.defaultIcon}
      onClick={iconAction.onClick}
    />
  );
};*/

interface StatusDropdownProps {
  statuses: VehicleCardInformationStatus[];
  currentStatus: string;
  onChange: (VehicleCardInformationStatus) => void;
}
const StatusDropdown = ({ statuses, currentStatus, onChange }: StatusDropdownProps) => {
  const { t } = useTranslation();
  return (
    <SimpleSelectDropdown
      onSelectionChange={(index) => onChange(statuses[index as number])}
      selectedIndex={statuses.findIndex((v) => v.key === currentStatus)}
      items={statuses.map((status) => {
        return { text: status.name, value: status.key };
      })}
      placement="bottom-left"
      mobileModalTitle={t('workflow.updateVehicleStatus')}
    />
  );
};

const renderAdditionalActionsButton = (additionalActions: VehicleAdditionalAction[]) => {
  if (!additionalActions || additionalActions.length === 0) return <></>;
  return (
    <Dropdown placement="bottom-right">
      <Dropdown.Trigger>
        <IconButton icon={<KebabMenuIcon />} />
      </Dropdown.Trigger>
      <Dropdown.Menu aria-label="additional-actions" css={{ minWidth: '0' }}>
        {additionalActions.map((action) => (
          <Dropdown.Item key={action.label}>
            <Text style={{ color: action.labelColor || 'auto' }} onClick={action.onClick}>
              {action.label}
            </Text>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export interface VehicleCardProps {
  listing: VehicleCardInformation;
  pill?: VehicleCardPill;
  boxes: Array<InfoBox>;
  actionOne?: VehicleCardAction;
  actionTwo?: VehicleCardAction;
  iconAction?: any;
  additionalActions?: VehicleAdditionalAction[];
  displayImageContainer?: boolean;
  statuses: VehicleCardInformationStatus[];
  currentStatus: string;
  onStatusChange?: (status: VehicleCardInformationStatus) => void;
}

const VehicleCard = ({
  listing,
  pill,
  boxes,
  actionOne,
  actionTwo,
  additionalActions,
  displayImageContainer = true,
  statuses = [],
  currentStatus = '',
  onStatusChange,
}: VehicleCardProps) => {
  const vehiclePicture = listing?.pictureUrl ?? defaultPicture;

  return (
    <Container>
      <VehicleImageContainer display={displayImageContainer}>
        <VehicleImage src={vehiclePicture} />
      </VehicleImageContainer>
      <VehicleInfoContainer>
        <VehicleHeaderContainer>
          <VehicleSmallImageContainer display={displayImageContainer}>
            <VehicleImage src={vehiclePicture} />
          </VehicleSmallImageContainer>
          <VehicleHeaderInformationContainer>
            <VehicleTitle>
              {listing?.trim ? [listing?.year, listing?.make, listing?.model].join(' ') : listing?.displayName}
            </VehicleTitle>
            <VehicleSubTitle>{listing?.trim}</VehicleSubTitle>
            {renderVehiclePill(pill)}
          </VehicleHeaderInformationContainer>
          <VehicleHeaderActionsContainer>
            <ButtonsContainer>
              <StatusDropdown statuses={statuses} currentStatus={currentStatus} onChange={onStatusChange} />
              {renderActionButton(actionTwo)}
              {renderActionButton(actionOne)}
            </ButtonsContainer>
            {/* TODO: Uncomment next line after implementing Add to favorite feature */}
            {/*renderIconActionButton(iconAction)*/}
            {renderAdditionalActionsButton(additionalActions)}
          </VehicleHeaderActionsContainer>
        </VehicleHeaderContainer>
        <VehicleBodyContainer $numberOfBoxes={boxes?.length}>
          {boxes?.map((box, index) => renderInfoBoxComponent(box, index))}
        </VehicleBodyContainer>
        <ButtonsMobileContainer>
          {renderActionButton(actionTwo)}
          {renderActionButton(actionOne)}
        </ButtonsMobileContainer>
      </VehicleInfoContainer>
    </Container>
  );
};

export default VehicleCard;
